import { Button } from "@ariakit/react";
import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useState,
} from "react";
import { lacquer, pillButton } from "../../common/common.css.ts";
import { diagram } from "./diagram.css.ts";

const PIPS = {
  2: [
    [0.25, 0.25],
    [0.75, 0.75],
  ],
  3: [
    [0.25, 0.25],
    [0.5, 0.5],
    [0.75, 0.75],
  ],
  4: [
    [0.25, 0.25],
    [0.75, 0.25],
    [0.25, 0.75],
    [0.75, 0.75],
  ],
  5: [
    [0.25, 0.25],
    [0.75, 0.25],
    [0.5, 0.5],
    [0.25, 0.75],
    [0.75, 0.75],
  ],
} satisfies Record<number, [number, number][]>;

interface Die {
  id: number;
  x: number;
  y: number;
  rotation: number;
  value: number;
  pips: [number, number][];
  used: boolean;
}

interface Point {
  x: number;
  y: number;
  value?: number;
}

const initialContext = {
  unit: 50,
  startPoint: { x: 60, y: 260 },
  initialDice: [
    { id: 0, x: 330, y: 70, value: 2, rotation: -6, pips: PIPS[2] },
    { id: 1, x: 600, y: 130, value: 5, rotation: 4, pips: PIPS[5] },
    { id: 2, x: 530, y: 300, value: 3, rotation: 10, pips: PIPS[3] },
  ] satisfies Omit<Die, "used">[],
  dieSize: 60,
  waypointRadius: 35,
  lineInset: 10,
};

const DiagramContext = createContext(initialContext);

function Waypoint({ x, y, label }: { x: number; y: number; label: string }) {
  const { waypointRadius } = useContext(DiagramContext);

  return (
    <g>
      <circle
        cx={x}
        cy={y}
        r={waypointRadius}
        className={diagram.waypointCircle}
      />
      <line
        x1={x - 10}
        y1={y - 10}
        x2={x + 10}
        y2={y + 10}
        className={diagram.waypointCross}
      />
      <line
        x1={x + 10}
        y1={y - 10}
        x2={x - 10}
        y2={y + 10}
        className={diagram.waypointCross}
      />
      <text x={x} y={y - waypointRadius - 6} className={diagram.waypointLabel}>
        {label}
      </text>
    </g>
  );
}

function Dice({ die, onUse }: { die: Die; onUse: (id: number) => void }) {
  const { dieSize } = useContext(DiagramContext);

  const x = die.x - dieSize / 2;
  const y = die.y - dieSize / 2;

  return (
    <g
      transform={`translate(${x}, ${y})`}
      className={diagram.die({ used: die.used })}
      style={{
        transformOrigin: `${die.x}px ${die.y}px`,
        rotate: `${die.rotation}deg`,
      }}
      onClick={() => !die.used && void onUse(die.id)}
    >
      <rect
        x="0"
        y="0"
        width={dieSize}
        height={dieSize}
        rx="8"
        className={diagram.dieRect({
          used: die.used,
        })}
      />
      {die.pips.map(([px, py], i) => (
        <circle
          key={i}
          cx={px * dieSize}
          cy={py * dieSize}
          r={dieSize * 0.075}
          className={diagram.diePip({ used: die.used })}
        />
      ))}
    </g>
  );
}

function ArrowMarker() {
  return (
    <defs>
      <marker
        id="arr"
        viewBox="0 0 10 10"
        refX="8"
        refY="5"
        markerWidth="6"
        markerHeight="6"
        orient="auto-start-reverse"
      >
        <path
          d="M2 1L8 5L2 9"
          fill="none"
          stroke="context-stroke"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </marker>
    </defs>
  );
}

function PathDiceMovement() {
  const { unit, initialDice, startPoint, lineInset } =
    useContext(DiagramContext);

  const [dice, setDice] = useState<Die[]>(
    initialDice.map((d) => ({ ...d, used: false })),
  );
  const [playerPos, setPlayerPos] = useState<Point>({ ...startPoint });
  const [trail, setTrail] = useState<Point[]>([{ ...startPoint }]);

  const reset = useCallback(() => {
    setDice(initialDice.map((d) => ({ ...d, used: false })));
    setPlayerPos({ ...startPoint });
    setTrail([{ ...startPoint }]);
  }, [initialDice, startPoint]);

  const selectDie = useCallback(
    (id: number) => {
      const selectedDice = dice.find((d) => d.id === id);
      if (!selectedDice || selectedDice.used) {
        return;
      }

      const angle = Math.atan2(
        selectedDice.y - playerPos.y,
        selectedDice.x - playerPos.x,
      );

      const dist = selectedDice.value * unit;

      const next: Point = {
        x: playerPos.x + dist * Math.cos(angle),
        y: playerPos.y + dist * Math.sin(angle),
        value: selectedDice.value,
      };

      setTrail((t) => [...t, next]);
      setPlayerPos(next);
      const nextDice = dice.map((d) =>
        d.id === id ? { ...d, used: true } : d,
      );
      setDice(nextDice);
    },
    [dice, playerPos.x, playerPos.y, unit],
  );

  function shortenEnd(x1: number, y1: number, x2: number, y2: number) {
    const angle = Math.atan2(y2 - y1, x2 - x1);
    return {
      x: x2 - lineInset * Math.cos(angle),
      y: y2 - lineInset * Math.sin(angle),
    };
  }

  const status =
    trail.length === 1
      ? "Choose any of the available dice to start!"
      : trail.length < 4
        ? `Choose any of the remaining dice...`
        : "All done! Reset?";

  const end = trail.at(3);

  return (
    <div id="movement-diagram" className={diagram.container}>
      <svg
        width="100%"
        viewBox="0 0 680 380"
        xmlns="http://www.w3.org/2000/svg"
      >
        <ArrowMarker />

        <text x={100} y={140} className={diagram.demoLabel}>
          Try me!
        </text>

        {/* Guide lines from player to unused dice */}
        {dice
          .filter((d) => !d.used)
          .map((d) => {
            const end = shortenEnd(playerPos.x, playerPos.y, d.x, d.y);
            return (
              <line
                key={`guide-${d.id}`}
                x1={playerPos.x}
                y1={playerPos.y}
                x2={end.x}
                y2={end.y}
                className={diagram.guideLine}
              />
            );
          })}

        {/* Traveled trail */}
        {trail.slice(1).map((pt, i) => {
          const prev = trail[i];
          const end = shortenEnd(prev?.x ?? 0, prev?.y ?? 0, pt.x, pt.y);
          return (
            <Fragment key={`trail-${i}`}>
              <line
                x1={prev?.x}
                y1={prev?.y}
                x2={end.x}
                y2={end.y}
                className={diagram.trailLine}
              />

              <text
                x={((trail[i]?.x ?? 0) + pt.x) / 2}
                y={((trail[i]?.y ?? 0) + pt.y) / 2 + 30}
                className={lacquer}
                style={{ fontSize: 24 }}
              >
                {pt.value}&quot;
              </text>
            </Fragment>
          );
        })}

        {dice.map((d) => (
          <Dice key={d.id} die={d} onUse={selectDie} />
        ))}

        <Waypoint x={startPoint.x} y={startPoint.y} label="Start" />

        {end && <Waypoint {...end} label="End" />}

        {/* Player marker */}
        <circle
          cx={playerPos.x}
          cy={playerPos.y}
          r="24"
          className={diagram.playerOuter}
        />
        <circle
          cx={playerPos.x}
          cy={playerPos.y}
          r="5"
          className={diagram.playerInner}
        />
      </svg>

      <div className={diagram.controls}>
        <div>
          <i>{status}</i>
        </div>
        <Button className={pillButton} onClick={reset}>
          Reset
        </Button>
      </div>
    </div>
  );
}

export function MovementDiagram(props: {
  config?: Partial<typeof initialContext>;
}) {
  const value = { ...initialContext, ...props.config };
  return (
    <DiagramContext value={value}>
      <PathDiceMovement />
    </DiagramContext>
  );
}
